/**
 * **Las operaciones de escritura de un informe de la pestaña FACTURACIÓN que todavía no se sabe cómo
 * acabaron**, guardadas en el navegador para que no se pierdan al recargar (COB-7, #193).
 *
 * POR QUÉ EXISTE. Opción **C** de la decisión técnica de @Donchurru del 15/10/2026: «conservar en el
 * navegador, antes del envío, la identidad completa usuario/acción/entidad/clave y el borrador
 * suficiente para reconciliar; recuperarlos después de recargar/cerrar/otra pestaña».
 *
 * Lo que cierra es concreto. La clave de una operación vivía **solo en el estado del componente**,
 * así que una recarga, una pestaña cerrada o un «ir atrás» a mitad de un guardado se llevaban por
 * delante lo único que permitía preguntar después qué había pasado. Al volver, la pantalla no sabía
 * que había algo a medias y lo siguiente que se pulsara iba a ciegas: con un alta, eso son dos
 * programaciones iguales mandando el informe dos veces.
 *
 * LA GARANTÍA SIGUE ACOTADA A UN INFORME, Y ESO ERA UNA CONDICIÓN DE LA DECISIÓN. Lo que se comparte
 * desde el 19/10/2026 es **el mecanismo**, no el almacén: `registroDe` da un registro por informe,
 * cada uno con su propio prefijo en el navegador, así que ninguno ve los apuntes del otro. Los
 * envíos y las programaciones de OEA y OEB no pasan por aquí ni cambian en nada.
 *
 * Y se comparte en vez de copiarse porque la copia es justo lo que falló: el diálogo de «Programar
 * reporte» del control diario (#549) llevaba la clave de su alta en el estado del componente, y la
 * revisión automática sacó la misma familia tres rondas seguidas —no había clave, la clave no
 * sobrevivía al borrado de la regla, la clave no sobrevivía a una recarga—. Aquí esos tres bordes
 * estaban resueltos, cada uno con su ronda pagada en #193.
 *
 * Y NADA SE APUNTA «A MEDIAS». `guardar` contesta si la operación ha quedado apuntada, y si dice que
 * no —no cabe, o el navegador no puede guardar— **la petición no se manda**. Es lo mismo por los dos
 * lados: una clave que no sobrevive a una recarga no es una clave, porque el siguiente intento lleva
 * otra y el servidor ya no puede reconocerlo como reintento. Más vale no hacer la operación que
 * hacerla sin poder saber después si se hizo.
 *
 * LO QUE NO HACE, Y ES LO MÁS IMPORTANTE. **No reenvía nada.** Recuperar una operación es poder
 * preguntar por ella y poder repetirla *si alguien lo pide*; repetirla sola al arrancar sería
 * mandar un correo que nadie ha pulsado. Lo pedía la decisión con esas palabras: «ni repostear
 * automáticamente al recuperar sesión».
 */

/**
 * **El registro de un informe.**
 *
 * @param {String} PREFIJO      dónde se guardan sus apuntes: **una entrada por operación**, con el
 *        nombre del informe delante y la clave de la operación detrás. Dos informes con prefijos
 *        distintos no se ven el uno al otro, que es lo que mantiene la garantía acotada.
 *
 *        UNA ENTRADA POR OPERACIÓN Y NO UN OBJETO CON TODAS, y esto es lo que cierra la avería que
 *        llevó tres rondas: guardándolas juntas, apuntar una era *leer el objeto entero, añadirle la
 *        clave y volver a escribirlo entero*, y eso entre dos pestañas no es atómico. Las dos leen lo
 *        mismo, las dos escriben después, y la segunda escritura **borra el apunte de la primera**
 *        aunque su petición ya hubiera salido. Esa operación se queda sin apunte: no se puede
 *        preguntar por ella, y repetirla lleva una clave nueva que el servidor no reconoce como
 *        reintento —dos programaciones iguales, o dos correos—.
 *
 *        Con una entrada por operación, apuntar es `setItem` de **su** clave y olvidar es
 *        `removeItem` de **su** clave: ninguna escritura toca el apunte de otra, así que el orden en
 *        que las dos pestañas lleguen deja de importar.
 * @param {String} CLAVE_DE_ANTES dónde las guardaba la versión anterior de ESTE informe: **un**
 *        objeto con todas dentro. Se pasa `null` en un informe que no tuvo esa versión —el control
 *        diario nunca guardó nada en el navegador—, y entonces no hay nada que convertir.
 *
 *        Hace falta en el que sí la tuvo porque un navegador puede tener apuntes de la versión
 *        anterior justo cuando se cambia de versión, y perderlos sería perder exactamente lo que esto
 *        promete conservar. Se traen a su entrada propia la primera vez que se lee y el objeto se
 *        borra **cuando todos han llegado**; si alguno no cupo, el objeto se queda porque es su única
 *        copia. Ver `traerLoDeLaVersionAnterior`.
 */
export function registroDe(PREFIJO, CLAVE_DE_ANTES) {

    /**
     * Cuántas puede tener a medias **una persona** como mucho.
     *
     * Hay tope porque esto es almacenamiento del navegador y no una cola. Pero el tope **no tira nada**:
     * cuando se llega, lo que se rechaza es la operación nueva, y quien la pidió se entera. Antes se
     * borraba la más antigua por edad, y eso era el defecto: aquí dentro **no hay ninguna resuelta** —una
     * operación se borra justamente cuando se resuelve, en `olvidar`—, así que la más antigua es siempre
     * una duda viva, y tirarla deja su clave perdida. Repetir la acción después crea otra operación
     * distinta, que es cómo salen dos programaciones iguales o dos correos.
     *
     * Y el tope es **por usuario**, no del almacén entero, por dos motivos. Los apuntes de otra persona
     * que use este navegador no son nuestros para tirarlos, y tampoco pueden bloquearnos: contándolos
     * todos juntos, veinte dudas suyas —que ella no ve hasta que vuelva a entrar— dejarían la pantalla
     * sin poder escribir a quien está delante ahora. Lo propio sí se puede resolver desde la pantalla,
     * en «Operaciones sin resolver», así que el tope nunca es un callejón sin salida.
     */
    const CUANTAS_COMO_MUCHO_POR_USUARIO = 20;

    /** Las claves del almacén del navegador que son apuntes de este informe. */
    function clavesApuntadas() {
        const claves = [];
        try {
            const almacen = window.localStorage;
            for (let i = 0; i < almacen.length; i++) {
                const clave = almacen.key(i);
                if (clave && clave.indexOf(PREFIJO) === 0) claves.push(clave);
            }
        } catch (noSePudoLeer) {
            // Leer el almacén puede lanzar con el almacenamiento desactivado. Sin apuntes legibles no
            // hay nada que recuperar, y es lo mismo que no tener ninguno.
            return [];
        }
        return claves;
    }

    /** Un apunte por su clave de almacén, o `null` si no está o no se puede leer. */
    function leerApunte(claveAlmacen) {
        try {
            const crudo = window.localStorage.getItem(claveAlmacen);
            const apunte = crudo ? JSON.parse(crudo) : null;
            // Un valor que no es un objeto es un apunte corrupto. Se descarta **ese** y nada más: con
            // todas en el mismo objeto, uno corrupto se llevaba por delante a las demás.
            return apunte && typeof apunte === 'object' && !Array.isArray(apunte) && apunte.clave
                ? apunte : null;
        } catch (noSePudoLeer) {
            return null;
        }
    }

    function escribirApunte(clave, apunte) {
        try {
            window.localStorage.setItem(PREFIJO + clave, JSON.stringify(apunte));
            return true;
        } catch (noSePudoEscribir) {
            // Un localStorage lleno o bloqueado (modo privado de Safari) no puede tumbar la pantalla,
            // así que no se lanza: se contesta que no se ha podido. Quien llama NO manda la petición,
            // porque sin el apunte la clave vive solo en memoria y una recarga la pierde: el siguiente
            // intento llevaría otra y el servidor no podría reconocerlo como reintento.
            console.warn('No se ha podido guardar la operación pendiente de ' + PREFIJO,
                noSePudoEscribir);
            return false;
        }
    }

    function borrarApunte(claveAlmacen) {
        try {
            window.localStorage.removeItem(claveAlmacen);
        } catch (noSePudoBorrar) {
            // Nada que hacer: lo que no se puede borrar se vuelve a preguntar al servidor, que
            // contestará que ya terminó. Molesta, pero no miente.
        }
    }

    /**
     * Pasa a su entrada propia lo que dejara apuntado la versión que las guardaba todas en un objeto, y
     * borra ese objeto **solo si todo lo que valía ha quedado a salvo**.
     *
     * Se hace al leer y no en un arranque, porque no hay ningún arranque: este módulo son tres
     * funciones. Es idempotente y no le importa que dos pestañas lo hagan a la vez —cada apunte se
     * escribe en **su** clave, así que escribirlo dos veces es escribir lo mismo— y nunca pisa un
     * apunte que ya tenga entrada propia: ese es el de ahora y manda sobre el del objeto viejo.
     *
     * EL ORIGEN NO SE BORRA HASTA QUE EL DESTINO ESTÁ ACREDITADO, y es la regla que falta cuando esto
     * se escribe «copiar y borrar». Mientras dura la conversión cada operación está **duplicada**, así
     * que es justo el momento en el que el almacén del navegador se puede quedar sin sitio: si
     * `escribirApunte` dice que no ha podido y el objeto viejo se borra igual, la única copia de esa
     * clave se va con él. Y una operación sin clave apuntada no se puede preguntar ni repetir como
     * reintento: el intento siguiente lleva otra clave y el servidor no lo reconoce —dos
     * programaciones iguales, o dos correos—.
     *
     * @return {Array} los apuntes que **siguen viviendo solo en el objeto viejo** porque no han podido
     *         pasar. No son basura: son operaciones sin resolver, y `todosLosApuntes` los devuelve
     *         igual para que la pantalla las vea y se puedan resolver.
     */
    function traerLoDeLaVersionAnterior() {
        const sinPasar = [];
        let deAntes = null;
        // Un informe sin versión anterior no tiene nada que convertir, y no se mira el almacén: con
        // la clave a nulo, `getItem(null)` leería la entrada literal «null» —que no es de nadie—.
        if (!CLAVE_DE_ANTES) return sinPasar;
        try {
            const crudo = window.localStorage.getItem(CLAVE_DE_ANTES);
            if (!crudo) return sinPasar;
            deAntes = JSON.parse(crudo);
        } catch (noSePudoLeer) {
            return sinPasar;
        }
        if (!deAntes || typeof deAntes !== 'object' || Array.isArray(deAntes)) {
            // No es el objeto que escribía la versión anterior, así que no hay ningún apunte dentro que
            // perder: ahí sí se puede borrar.
            borrarApunte(CLAVE_DE_ANTES);
            return sinPasar;
        }
        let todoASalvo = true;
        Object.keys(deAntes).forEach(function(clave) {
            const apunte = deAntes[clave];
            // Lo que no es un apunte tampoco impide borrar el objeto: no hay nada que salvar.
            if (!apunte || typeof apunte !== 'object' || !apunte.clave) return;
            if (leerApunte(PREFIJO + apunte.clave) !== null) return;
            if (escribirApunte(apunte.clave, apunte)) return;
            todoASalvo = false;
            sinPasar.push(apunte);
        });
        if (todoASalvo) borrarApunte(CLAVE_DE_ANTES);
        return sinPasar;
    }

    /**
     * Saca una operación **de dentro del objeto de la versión anterior**, cuando ese objeto ha
     * sobrevivido a la conversión porque algo no cupo en su entrada.
     *
     * Hace falta por lo mismo que el objeto se conserva: si la operación se resuelve y sigue ahí
     * dentro, el aviso vuelve a salir al recargar ya resuelto. Esto sí es *leer, quitar y volver a
     * escribir* —lo que se dejó de hacer con los apuntes de ahora, y por buenas razones—, pero aquí el
     * peor caso al perder la carrera es el contrario y es el bueno: queda dentro un apunte **ya
     * resuelto**, que vuelve a preguntarse y el servidor contesta que terminó. Molesta y no miente.
     * Perder una clave sin resolver sí miente, y es lo que esto evita.
     */
    function quitarDeLaVersionAnterior(clave) {
        let deAntes = null;
        if (!CLAVE_DE_ANTES) return;
        try {
            const crudo = window.localStorage.getItem(CLAVE_DE_ANTES);
            if (!crudo) return;
            deAntes = JSON.parse(crudo);
        } catch (noSePudoLeer) {
            return;
        }
        if (!deAntes || typeof deAntes !== 'object' || Array.isArray(deAntes)) return;
        const quedan = {};
        Object.keys(deAntes).forEach(function(suya) {
            const apunte = deAntes[suya];
            if (suya === clave || (apunte && apunte.clave === clave)) return;
            quedan[suya] = apunte;
        });
        const cuantas = Object.keys(quedan).length;
        if (cuantas === Object.keys(deAntes).length) return;
        if (!cuantas) {
            borrarApunte(CLAVE_DE_ANTES);
            return;
        }
        try {
            window.localStorage.setItem(CLAVE_DE_ANTES, JSON.stringify(quedan));
        } catch (noSePudoEscribir) {
            // Sigue sin caber. El apunte resuelto se queda dentro y su aviso puede volver a salir al
            // recargar; se vuelve a preguntar y el servidor dirá que terminó.
        }
    }

    /**
     * Todos los apuntes que hay en este navegador, de quien sea.
     *
     * **Por aquí pasan los tres de fuera** —`guardar`, `pendientesDe` y el tope—, y es a propósito: es
     * el único sitio donde se decide qué apuntes existen, así que lo que no haya podido pasar a su
     * entrada se ve igual y no hay ninguna vía que lo deje fuera por su cuenta.
     */
    function todosLosApuntes() {
        const sinPasar = traerLoDeLaVersionAnterior();
        const apuntes = clavesApuntadas()
            .map(function(claveAlmacen) { return leerApunte(claveAlmacen); })
            .filter(function(apunte) { return !!apunte; });
        if (!sinPasar.length) return apuntes;
        const yaEstan = {};
        apuntes.forEach(function(apunte) { yaEstan[apunte.clave] = true; });
        sinPasar.forEach(function(apunte) {
            if (!yaEstan[apunte.clave]) apuntes.push(apunte);
        });
        return apuntes;
    }

    /** Cuántas operaciones sin resolver tiene apuntadas esa persona. */
    function cuantasTiene(apuntes, usuario) {
        return apuntes.filter(function(apunte) { return apunte.usuario === usuario; }).length;
    }

    return {
        /**
         * Apunta una operación **antes de lanzarla**.
         *
         * Antes y no después, que es toda la gracia: lo que hay que poder recuperar es justo la
         * operación cuya respuesta no llegó, y si se apuntara al volver la respuesta, el caso que
         * importa sería el único que no quedaría apuntado.
         *
         * Se guarda en **una entrada del almacén por operación**, y es lo único que de verdad impide que
         * dos pestañas se pisen el apunte: escribir el de una no vuelve a escribir el de las demás. El
         * sitio de la pantalla viaja dentro, en `ambito`.
         *
         * @param {Object} entrada `{ambito, usuario, accion, entidad, clave, borrador}`.
         *        - `ambito`: a qué parte de la pantalla vuelve el aviso (`ENVIO`, `FORM`, `regla:<id>`).
         *        - `usuario`: quién la pidió. Sin esto, el apunte de quien usó antes este navegador se
         *          le enseñaría al siguiente, con su borrador y sus destinatarios dentro.
         *        - `accion`: `ENVIO`, `ALTA`, `EDICION`, `ALTERNAR`, `BORRADO` o `ENVIO_REGLA`.
         *        - `entidad`: la regla sobre la que va, o `null`.
         *        - `clave`: la `operacion` con la que viajó, que es lo que se consulta después.
         *        - `borrador`: lo que hace falta para **repetirla igual** y para contar qué era.
         *        - `intentadaEn`: cuándo salió **este** intento. Lo pone quien lanza, justo antes de
         *          llamar aquí, y se guarda tal cual: a diferencia de `cuando`, un reintento **sí** lo
         *          mueve, porque lo que dice es «hace cuánto salió el POST que está de camino» y no
         *          «cuándo empezó la operación». Con el de la operación, una que se reintenta media hora
         *          después parecería antigua con su petición recién salida.
         * @return {boolean} `true` si la operación queda apuntada y se puede lanzar. **`false` quiere
         *         decir que NO se puede lanzar**: o no cabe, o el navegador no ha podido guardarla. Quien
         *         llama tiene que mirarlo y no mandar la petición, porque sin el apunte la clave vive solo
         *         en memoria: una recarga o una pestaña cerrada se la lleva, el siguiente intento usa otra
         *         y el servidor ya no puede reconocerlo como reintento —ahí es donde salen dos
         *         programaciones iguales o dos correos—.
         */
        guardar: function(entrada) {
            if (!entrada || !entrada.clave) return false;
            const apuntes = todosLosApuntes();
            const anterior = leerApunte(PREFIJO + entrada.clave);
            // Un reintento de la MISMA operación no cuenta: su clave ya está apuntada y lo único que hace
            // esto es refrescarla. El tope solo mira las altas de una clave nueva.
            if (!anterior && cuantasTiene(apuntes, entrada.usuario) >= CUANTAS_COMO_MUCHO_POR_USUARIO) {
                return false;
            }
            // Y un reintento **no cambia cuándo empezó** la operación. Ese instante es lo que ordena los
            // avisos y lo que decide cuál ocupa cada control: refrescándolo, repetir la más antigua la
            // colaría detrás de las nuevas y lo que se enseña delante dejaría de ser lo suyo.
            //
            // Son dos instantes distintos y por eso no se mezclan: `cuando` es de la OPERACIÓN y se
            // congela; `intentadaEn` es de ESTE intento, viaja en la entrada y se mueve con cada
            // reintento, porque es con el que se sabe si el POST todavía puede estar de camino.
            const cuando = (anterior && anterior.cuando) || Date.now();
            // Lo que devuelve `escribirApunte` es lo que devuelve esto: si el navegador no ha podido
            // guardarlo —cuota llena, almacenamiento bloqueado, modo privado—, la operación no está
            // apuntada en ningún sitio y decir que sí sería anunciar una garantía que no está.
            return escribirApunte(entrada.clave, Object.assign({}, entrada, { cuando: cuando }));
        },

        /**
         * **Vuelve a apuntar una operación que ya salió**, cuando su apunte ha desaparecido del almacén
         * sin que ella se haya resuelto.
         *
         * POR QUÉ NO BASTA `guardar`. Porque `guardar` tiene el tope por usuario, y el tope existe para
         * **rechazar una operación nueva**: con veinte sin resolver, lanzar la veintiuna sería lanzar
         * algo que después no se puede reconciliar. Aquí la operación ya está lanzada y sin resolver, así
         * que rechazar el apunte no la deshace: lo único que consigue es perder su clave, que es
         * exactamente el daño que el tope quería evitar. Así que esto apunta sin mirar el tope.
         *
         * QUIÉN LA PIERDE SIN RESOLVERLA. Dos pestañas comparten el almacén y **comparten la clave** de
         * una operación recuperada: si la pestaña A termina bien la suya y olvida su apunte mientras B
         * tiene la misma clave en vuelo o acaba de recibir un resultado ambiguo, el apunte que B necesita
         * ya no está. También se lo lleva un `clear()` del navegador. En los dos casos la operación de B
         * sigue sin resolverse y su clave tiene que volver al almacén, o una recarga la perdería y lo
         * siguiente que se pulse crearía otra operación —otro correo, otra programación—.
         *
         * Lo que NO hace es borrar: un apunte que falta puede ser de una operación que otra pestaña está
         * lanzando ahora mismo, así que reponer de más no rompe nada —se vuelve a preguntar al servidor y
         * contesta que terminó— y borrar de más sí.
         *
         * @return {boolean} `true` si ha quedado apuntada. `false` es que el navegador no puede guardar,
         *         y entonces quien está delante tiene que saberlo: su clave vive solo en esta pestaña.
         */
        reapuntar: function(entrada) {
            if (!entrada || !entrada.clave) return false;
            const anterior = leerApunte(PREFIJO + entrada.clave);
            const cuando = (anterior && anterior.cuando) || entrada.cuando || Date.now();
            return escribirApunte(entrada.clave, Object.assign({}, entrada, { cuando: cuando }));
        },

        /**
         * **Si esa clave está apuntada en este navegador.**
         *
         * Se contesta aquí —y por `todosLosApuntes`, como los otros tres— porque este módulo es el único
         * sitio donde se decide qué apuntes existen: cuentan la entrada por operación de ahora y el
         * objeto de la versión anterior, que puede seguir ahí si algún apunte no cupo. Preguntándolo
         * desde fuera con un `getItem`, una operación que todavía viviera dentro de ese objeto se daría
         * por perdida y se avisaría de algo que sí está guardado.
         */
        tieneApunte: function(clave) {
            if (!clave) return false;
            return todosLosApuntes().some(function(apunte) { return apunte.clave === clave; });
        },

        /**
         * Las operaciones apuntadas **de esa persona**, las más antiguas primero.
         *
         * Las de otro usuario de este mismo navegador no se devuelven y **no se borran**: no son
         * basura, son sus operaciones a medias, y se las encontrará cuando vuelva a entrar. Lo que no
         * puede pasar es que se le enseñen a quien está delante ahora, porque llevan dentro su
         * borrador. El servidor, además, tampoco contestaría por ellas: la clave que consulta la
         * compone con el usuario autenticado.
         */
        pendientesDe: function(usuario) {
            if (!usuario) return [];
            return todosLosApuntes()
                .filter(function(entrada) { return entrada.usuario === usuario; })
                .sort(function(a, b) { return (a.cuando || 0) - (b.cuando || 0); });
        },

        /**
         * **Si una clave del almacén del navegador es de este registro.**
         *
         * La pregunta la hace quien escucha los cambios de otra pestaña: el navegador avisa de
         * **todas** las claves que cambian, y la mayoría no son de aquí.
         *
         * Se contesta aquí y no comparando con una cadena escrita en la pantalla, por lo mismo que la
         * lista de timeouts de la pantalla es la de `DatabaseModel`: dos sitios que nombran el almacén
         * por separado acaban diciendo cosas distintas, y el día que el prefijo cambie el que se quede
         * viejo deja de enterarse de lo que haga la otra pestaña —sin fallar, que es lo peor—.
         *
         * Las dos formas cuentan: la entrada por operación de ahora y el objeto de la versión anterior,
         * que puede seguir ahí si algo no cupo (ver `traerLoDeLaVersionAnterior`).
         */
        esSuya: function(clave) {
            if (!clave) return false;
            const texto = String(clave);
            return texto.indexOf(PREFIJO) === 0 || (!!CLAVE_DE_ANTES && texto === CLAVE_DE_ANTES);
        },

        /**
         * Deja de preguntar por esa operación.
         *
         * **Solo se llama con la finalización acreditada**: cuando el servidor ha contestado que
         * terminó, o que no consta ningún efecto, o cuando una persona ha dicho expresamente que la da
         * por vista. Olvidarla por su cuenta es lo que deja repetir a ciegas, que es el defecto que
         * esto viene a cerrar.
         */
        olvidar: function(clave) {
            if (!clave) return;
            // Antes de borrar, lo de la versión anterior pasa a su entrada propia: si no, un apunte que
            // todavía viviera dentro del objeto viejo no se borraría nunca y el aviso volvería a salir
            // al recargar, ya resuelto.
            traerLoDeLaVersionAnterior();
            borrarApunte(PREFIJO + clave);
            // Y si ese objeto ha sobrevivido —porque algún apunte no cupo en su entrada—, esta
            // operación sale también de dentro, por el mismo motivo.
            quitarDeLaVersionAnterior(clave);
        }
    };
}

/**
 * **El registro del informe de abonos abiertos**, que es donde nació (COB-7, #193).
 *
 * Se exporta por omisión para que su pantalla no cambie en nada: el prefijo del almacén y la clave
 * de la versión anterior son los mismos de siempre, así que los apuntes que haya en el navegador de
 * alguien siguen siendo suyos y se siguen leyendo igual.
 */
export default registroDe('factcobro-abonos-operaciones:', 'factcobro-abonos-operaciones');
